// The menu on a channel row or a DM row in the sidebar (#153), the same in both layouts: right-click on desktop,
// long-press on a phone (#151), the Menu key or Shift+F10 from the keyboard. One menu: the notification menu
// (NotifyBell.tsx's NotifyMenu: Mute or Unmute, the level) with the row's own items around it: Mark as read before,
// Copy link, Channel settings… and Close conversation after. Phones show it in a sheet (Server.tsx); desktop in
// DesktopRowMenu, a popover at the pointer. Rows only call `rowMenuHandlers`; Server.tsx owns the menu.
import { useEffect, useLayoutEffect, useRef, useState, type HTMLAttributes, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { isTouch, longPress } from "../mobile.ts";
import type { RowAction } from "../rowMenuLogic.ts";
import "./row-menu.css";

/** Where the menu opens from: the row (to give focus back to), and the pointer for a right-click. */
export interface MenuOrigin {
  from: HTMLElement;
  at?: { x: number; y: number };
  keyboard?: boolean;
  /** A long press: always the sheet. */
  touch?: boolean;
}

/** A row asks for its menu. */
export type OpenRowMenu = (channelId: string, origin: MenuOrigin) => void;

const HOLD_MS = 500;

/** What a row spreads on its link: long press on touch screens, right-click, and the Menu key or Shift+F10. */
export function rowMenuHandlers(open: OpenRowMenu, channelId: string): HTMLAttributes<HTMLElement> {
  return {
    ...(isTouch() ? longPress((el) => open(channelId, { from: el, touch: true }), HOLD_MS) : {}),
    onContextMenu: (e) => {
      // A phone's own long-press menu would sit over ours (the press above opens ours).
      e.preventDefault();
      if (isTouch()) return;
      open(channelId, { from: e.currentTarget, at: { x: e.clientX, y: e.clientY } });
    },
    onKeyDown: (e) => {
      if (e.key !== "ContextMenu" && !(e.shiftKey && e.key === "F10")) return;
      e.preventDefault();
      open(channelId, { from: e.currentTarget, keyboard: true });
    },
  };
}

const ICONS: Record<RowAction, ReactNode> = {
  read: <><circle cx="12" cy="12" r="9" /><path d="m8 12.5 3 3 5-6" /></>,
  link: <><path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" /><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" /></>,
  settings: <><path d="M4 6h8M16 6h4M4 12h2M10 12h10M4 18h10M18 18h2" /><circle cx="14" cy="6" r="2" /><circle cx="8" cy="12" r="2" /><circle cx="16" cy="18" r="2" /></>,
  close: <><circle cx="12" cy="12" r="9" /><path d="m15 9-6 6M9 9l6 6" /></>,
};
const LABELS: Record<RowAction, string> = { read: "Mark as read", link: "Copy link", settings: "Channel settings…", close: "Close conversation" };

/** The row's own items, as a group of the notification menu's rows. `run` does the item; the menu closes after it. */
export function RowActionGroup({ actions, label, run, onClose }: { actions: RowAction[]; label: string; run: Record<RowAction, () => void>; onClose: () => void }) {
  if (!actions.length) return null;
  return (
    <div className="row-actions" role="group" aria-label={label}>
      {actions.map((a) => (
        <button
          key={a}
          type="button"
          role="menuitem"
          className={`notify-option row-action${a === "close" ? " danger" : ""}`}
          onClick={() => {
            run[a]();
            onClose();
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ICONS[a]}</svg>
          <span className="notify-option-text">
            <span className="notify-option-label">{LABELS[a]}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

/** Keeps the row lit while its menu is open. */
export function useRowLit(from: HTMLElement) {
  useEffect(() => {
    from.classList.add("menu-open");
    return () => from.classList.remove("menu-open");
  }, [from]);
}

const W = 320;
const EDGE = 8;

/**
 * Desktop: the menu in a popover at the pointer (its corner there, flipped left or up near the window's edges), or
 * under the row when opened from the keyboard. Arrow keys move between its buttons, Escape closes it and gives focus
 * back to the row, a click elsewhere or a scroll of the list closes it.
 */
export function DesktopRowMenu({ origin, label, onClose, children }: { origin: MenuOrigin; label: string; onClose: (refocus: boolean) => void; children: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<React.CSSProperties>({ position: "fixed", top: origin.at?.y ?? 0, left: origin.at?.x ?? 0, opacity: 0 });
  useRowLit(origin.from);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const h = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (origin.at) {
      const { x, y } = origin.at;
      setPos({ position: "fixed", top: y + h <= vh - EDGE ? y : Math.max(EDGE, y - h), left: x + W <= vw - EDGE ? x : Math.max(EDGE, x - W) });
    } else {
      const a = origin.from.getBoundingClientRect();
      const below = a.bottom + 2;
      const above = a.top - 2 - h;
      setPos({ position: "fixed", top: below + h <= vh - EDGE ? below : above >= EDGE ? above : Math.max(EDGE, vh - EDGE - h), left: Math.max(EDGE, Math.min(a.left + EDGE, vw - EDGE - W)) });
    }
    (origin.keyboard ? el.querySelector<HTMLElement>("button") : el)?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const onDown = (e: MouseEvent) => !(e.target instanceof Node && box.current?.contains(e.target)) && onClose(false);
    // Only the list the row is in scrolling takes the menu away from it (not the app shell, which shifts when focus moves).
    const list = origin.from.closest(".sidebar-scroll");
    const onScroll = (e: Event) => e.target === list && onClose(false);
    const gone = () => onClose(false);
    // Escape with focus elsewhere (the button pressed went away with its view, and focus with it) still closes it.
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || box.current?.contains(document.activeElement)) return;
      e.preventDefault();
      onClose(true);
    };
    document.addEventListener("keydown", onEsc);
    // Next tick, so the click that opened it doesn't close it.
    const t = setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    window.addEventListener("resize", gone);
    window.addEventListener("blur", gone);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("resize", gone);
      window.removeEventListener("blur", gone);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, []);

  const onKey = (e: React.KeyboardEvent) => {
    const items = [...(box.current?.querySelectorAll<HTMLElement>("button:not(:disabled)") ?? [])];
    const at = items.indexOf(document.activeElement as HTMLElement);
    const move = (i: number) => items[(i + items.length) % items.length]?.focus();
    if (e.key === "Escape" || e.key === "Tab") {
      e.preventDefault();
      e.stopPropagation();
      onClose(true);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      move(at + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      move(at < 0 ? items.length - 1 : at - 1);
    } else if (e.key === "Home" || e.key === "End") {
      e.preventDefault();
      move(e.key === "Home" ? 0 : items.length - 1);
    }
  };

  // row-menu: an owner of Esc for the shortcuts (shortcuts.ts).
  return createPortal(
    <div className="row-menu" ref={box} style={{ ...pos, width: W }} tabIndex={-1} aria-label={label} onKeyDown={onKey}>
      {children}
    </div>,
    document.body,
  );
}
